import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { Bar, BarChart, CartesianGrid, Cell, Line, LineChart, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { toast } from "sonner";
import { PageHeader, Stat } from "./shared";

const TIER_COLORS = { sapling: "#B8860B", canopy: "#0F4C15", ridge: "#1A1A1A", cordillera: "#8B0000" };
const STATUS_COLORS = { pending: "#B8860B", confirmed: "#0F4C15", completed: "#08300E", cancelled: "#8B0000" };

export default function AnalyticsTab() {
    const { t } = useT();
    const [data, setData] = useState(null);
    const [suggestions, setSuggestions] = useState(null);
    const [applyingId, setApplyingId] = useState(null);

    const refreshSuggestions = () => api.get("/admin/pricing-suggestions").then((r) => setSuggestions(r.data));
    useEffect(() => {
        api.get("/admin/analytics").then((r) => setData(r.data));
        refreshSuggestions();
    }, []);

    const applySuggestion = async (s) => {
        setApplyingId(s.cabin_id);
        try {
            const cabin = (await api.get(`/cabins/${s.cabin_id}`)).data;
            const payload = {
                name: cabin.name,
                slug: cabin.slug,
                tagline: cabin.tagline,
                description: cabin.description,
                price_per_night: s.suggested_price,
                max_guests: cabin.max_guests,
                bedrooms: cabin.bedrooms,
                bathrooms: cabin.bathrooms,
                size_sqft: cabin.size_sqft,
                amenities: cabin.amenities || [],
                images: cabin.images || [],
                featured: !!cabin.featured,
                active: cabin.active !== false,
            };
            await api.put(`/cabins/${s.cabin_id}`, payload);
            toast.success(t("admin.pricing.applied"));
            refreshSuggestions();
        } catch (e) {
            toast.error(e.response?.data?.detail || t("toast.saveFailed"));
        } finally {
            setApplyingId(null);
        }
    };

    const applyAll = async () => {
        if (!suggestions?.suggestions?.length) return;
        for (const s of suggestions.suggestions) {
            if (Math.abs(s.delta_pct) >= 1) {
                // eslint-disable-next-line no-await-in-loop
                await applySuggestion(s);
            }
        }
    };

    if (!data) return <div data-testid="admin-analytics"><PageHeader eyebrow={`Admin · ${t("admin.analytics")}`} title={t("admin.insights")} /><div className="font-serif text-2xl text-espresso">{t("common.loading")}</div></div>;

    const fmt = (n) => `$${Number(n || 0).toLocaleString()}`;

    return (
        <div data-testid="admin-analytics">
            <PageHeader eyebrow={`Admin · ${t("admin.analytics")}`} title={t("admin.insights")} />

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
                <Stat label={t("admin.analyticsLabels.revenue6m")} value={fmt(data.totals.revenue_last_6m)} accent />
                <Stat label={t("admin.analyticsLabels.revenueAllTime")} value={fmt(data.totals.revenue_all_time)} />
                <Stat label={t("admin.analyticsLabels.averageBooking")} value={fmt(data.totals.average_order_value)} />
                <Stat label={t("admin.analyticsLabels.totalBookings")} value={data.totals.total_bookings} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10">
                {/* Revenue trend */}
                <div className="lg:col-span-2 border border-espresso/15 p-6" data-testid="analytics-revenue">
                    <div className="eyebrow mb-4">{t("admin.analyticsLabels.revenue6m")}</div>
                    <div style={{ width: "100%", height: 280 }}>
                        <ResponsiveContainer>
                            <LineChart data={data.monthly_revenue} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                                <CartesianGrid stroke="#EFEBE6" />
                                <XAxis dataKey="month" stroke="#1A1A1A" fontSize={11} />
                                <YAxis stroke="#1A1A1A" fontSize={11} />
                                <Tooltip formatter={(v) => fmt(v)} contentStyle={{ background: "#FAF8F5", border: "1px solid #1A1A1A", borderRadius: 0 }} />
                                <Line type="monotone" dataKey="revenue" stroke="#B8860B" strokeWidth={2} dot={{ r: 4, fill: "#0F4C15" }} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Status distribution */}
                <div className="border border-espresso/15 p-6" data-testid="analytics-status">
                    <div className="eyebrow mb-4">{t("admin.analyticsLabels.bookingsByStatus")}</div>
                    <div style={{ width: "100%", height: 280 }}>
                        <ResponsiveContainer>
                            <PieChart>
                                <Pie data={data.status_distribution} dataKey="count" nameKey="status" cx="50%" cy="50%" outerRadius={90} label={(e) => t(`admin.analyticsLabels.statusLabels.${e.status}`) || e.status}>
                                    {data.status_distribution.map((entry) => (
                                        <Cell key={entry.status} fill={STATUS_COLORS[entry.status] || "#1A1A1A"} />
                                    ))}
                                </Pie>
                                <Tooltip contentStyle={{ background: "#FAF8F5", border: "1px solid #1A1A1A", borderRadius: 0 }} />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10">
                {/* Top cabins */}
                <div className="lg:col-span-2 border border-espresso/15 p-6" data-testid="analytics-top-cabins">
                    <div className="eyebrow mb-4">{t("admin.analyticsLabels.topCabins")}</div>
                    <div style={{ width: "100%", height: 300 }}>
                        <ResponsiveContainer>
                            <BarChart data={data.top_cabins} margin={{ top: 10, right: 20, left: 0, bottom: 30 }}>
                                <CartesianGrid stroke="#EFEBE6" />
                                <XAxis dataKey="name" stroke="#1A1A1A" fontSize={11} interval={0} angle={-15} textAnchor="end" />
                                <YAxis stroke="#1A1A1A" fontSize={11} />
                                <Tooltip formatter={(v) => fmt(v)} contentStyle={{ background: "#FAF8F5", border: "1px solid #1A1A1A", borderRadius: 0 }} />
                                <Bar dataKey="revenue" fill="#0F4C15" />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Tier distribution */}
                <div className="border border-espresso/15 p-6" data-testid="analytics-tiers">
                    <div className="eyebrow mb-4">{t("admin.analyticsLabels.guestsByTier")}</div>
                    <div className="space-y-4 mt-6">
                        {data.tier_distribution.map((tier) => {
                            const total = data.tier_distribution.reduce((s, x) => s + x.count, 0) || 1;
                            const pct = Math.round((tier.count / total) * 100);
                            return (
                                <div key={tier.key}>
                                    <div className="flex justify-between text-sm mb-1">
                                        <span className="font-serif text-lg">{tier.name}</span>
                                        <span className="text-muted2">{tier.count} · {pct}%</span>
                                    </div>
                                    <div className="h-2 bg-bone2">
                                        <div className="h-2" style={{ width: `${pct}%`, backgroundColor: TIER_COLORS[tier.key] || "#0F4C15" }} />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Dynamic pricing suggestions */}
            <div className="border border-espresso/15 p-6 lg:p-8 mt-2" data-testid="analytics-pricing-suggestions">
                <div className="flex justify-between items-end mb-6 flex-wrap gap-4">
                    <div>
                        <div className="eyebrow mb-2">{t("admin.pricing.eyebrow")}</div>
                        <h2 className="font-serif text-3xl">{t("admin.pricing.title")}</h2>
                    </div>
                    {suggestions?.suggestions?.some((s) => Math.abs(s.delta_pct) >= 1) && (
                        <button
                            onClick={applyAll}
                            data-testid="pricing-apply-all"
                            className="px-5 py-3 bg-forest text-bone hover:bg-forestDark text-xs uppercase tracking-widest2 font-semibold"
                        >
                            {t("admin.pricing.applyAll")}
                        </button>
                    )}
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="text-left text-xs uppercase tracking-widest2 text-espresso bg-bone2/50">
                            <tr>
                                <th className="px-4 py-3">{t("admin.pricing.cols.cabin")}</th>
                                <th className="px-4 py-3">{t("admin.pricing.cols.occupancy")}</th>
                                <th className="px-4 py-3">{t("admin.pricing.cols.current")}</th>
                                <th className="px-4 py-3">{t("admin.pricing.cols.suggested")}</th>
                                <th className="px-4 py-3">{t("admin.pricing.cols.delta")}</th>
                                <th className="px-4 py-3">{t("admin.pricing.cols.reason")}</th>
                                <th className="px-4 py-3" />
                            </tr>
                        </thead>
                        <tbody>
                            {!suggestions ? (
                                <tr><td colSpan={7} className="px-4 py-8 text-muted2">{t("common.loading")}</td></tr>
                            ) : suggestions.suggestions.map((s) => {
                                const noChange = Math.abs(s.delta_pct) < 1;
                                const deltaCls = s.delta_pct > 0 ? "text-forest" : s.delta_pct < 0 ? "text-terracotta" : "text-muted2";
                                return (
                                    <tr key={s.cabin_id} className="border-t border-espresso/10" data-testid={`pricing-row-${s.cabin_id}`}>
                                        <td className="px-4 py-4 font-medium">{s.name}</td>
                                        <td className="px-4 py-4">
                                            <div className="text-base">{s.occupancy_pct}%</div>
                                            <div className="text-xs text-muted2">{t("admin.pricing.nightsBooked", s.nights_booked_30d)}</div>
                                        </td>
                                        <td className="px-4 py-4 text-muted2">${s.current_price}</td>
                                        <td className="px-4 py-4 font-serif text-xl">${s.suggested_price}</td>
                                        <td className={`px-4 py-4 ${deltaCls}`}>{s.delta_pct > 0 ? "+" : ""}{s.delta_pct}%</td>
                                        <td className="px-4 py-4 text-xs text-muted2">{t(`admin.pricing.reason${s.reason_key.split("_").map((p) => p.charAt(0).toUpperCase() + p.slice(1)).join("")}`)}</td>
                                        <td className="px-4 py-4 text-right">
                                            {noChange ? (
                                                <span className="text-xs uppercase tracking-widest2 text-muted2">{t("admin.pricing.noChange")}</span>
                                            ) : (
                                                <button
                                                    onClick={() => applySuggestion(s)}
                                                    disabled={applyingId === s.cabin_id}
                                                    data-testid={`pricing-apply-${s.cabin_id}`}
                                                    className="px-4 py-2 bg-forest text-bone hover:bg-forestDark text-[10px] uppercase tracking-widest2 font-semibold disabled:opacity-50"
                                                >
                                                    {applyingId === s.cabin_id ? "…" : t("admin.pricing.apply")}
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
